import { formatCoding, getExtensionValue, getIdentifier } from "@medplum/core";

import { addDays, format, parseISO } from "date-fns";

import { Extensions } from "@/fhir/enums/extensions";
import { Systems } from "@/fhir/enums/systems";
import {
  calculateGestationalAgeInWeeksAndDays,
  getGestationalAgeDisplay,
  PregnancyRecord,
  USUAL_GESTATION_DAYS,
} from "@/pregnancy";
import { usDateFormat } from "@/utils/datetime";
import {
  getCodeableConceptCode,
  getCodeableConceptDisplay,
  joinReferenceString,
} from "@/utils/medplum";

import { EncounterTypeFlowsheetMap } from "../constants/exportOptions";
import {
  EncounterData,
  EncounterOption,
  FileData,
  FileOption,
  LabOption,
  LabOrderData,
} from "../types/export";

/**
 * Helper function to check if an encounter type has a flowsheet.
 * @param encounterServiceTypeCode - The code of the encounter service type
 * @returns true if the encounter type has a flowsheet, false otherwise, or undefined if not found
 */
export function hasEncounterFlowsheet(
  encounterServiceTypeCode: string | undefined,
): boolean | undefined {
  if (!encounterServiceTypeCode) {
    return undefined;
  }
  return EncounterTypeFlowsheetMap.find((item) => item.code === encounterServiceTypeCode)
    ?.hasFlowsheet;
}

/**
 * Finds the pregnancy record whose `List.entry` references the given encounter.
 * Returns undefined if the encounter is not linked to any pregnancy record.
 *
 * @param pregnancyRecords - All known pregnancy records for the patient
 * @param encounterId - The encounter resource ID to look up
 */
export function findPregnancyRecordForEncounter(
  pregnancyRecords: PregnancyRecord[],
  encounterId: string,
): PregnancyRecord | undefined {
  return pregnancyRecords.find((record) =>
    record.list.entry?.some(
      (entry) => entry.item?.reference === joinReferenceString("Encounter", encounterId),
    ),
  );
}

/**
 * Transforms encounter data into encounter options for the export form.
 * Calculates gestational age per encounter from its linked PregnancyRecord (EDD or LMP).
 * This ensures correct GA display for multi-pregnancy scenarios where each encounter
 * belongs to a different pregnancy.
 *
 * @param encountersData - Array of encounter data with related observations and episodes
 * @param pregnancyRecords - All pregnancy records for the patient (current and historical).
 *   Pass an empty array for patients without any PregnancyRecord.
 * @returns Array of EncounterOption objects
 */
export function mapEncounterDataToOptions(
  encountersData: EncounterData[],
  pregnancyRecords: PregnancyRecord[] = [],
): EncounterOption[] {
  return encountersData.map(({ encounter, episodeOfCare }) => {
    const encounterServiceTypeDisplay = getCodeableConceptDisplay(encounter.serviceType);
    const encounterTypeDisplay =
      getCodeableConceptDisplay(encounter.type?.[0]) ||
      getCodeableConceptDisplay(encounter.reasonCode?.[0]);
    const encounterClassDisplay = encounter.class?.display;
    const encounterDate = encounter.period?.start
      ? format(new Date(encounter.period.start), "MM/dd/yyyy")
      : undefined;

    const linkedRecord = findPregnancyRecordForEncounter(pregnancyRecords, encounter.id ?? "");

    // Derive EDD from the linked record: prefer stored EDD, otherwise derive from LMP.
    const eddString =
      linkedRecord?.edd ??
      (linkedRecord?.lastMenstrualPeriod
        ? format(
            addDays(parseISO(linkedRecord.lastMenstrualPeriod), USUAL_GESTATION_DAYS),
            "yyyy-MM-dd",
          )
        : undefined);

    const gestationalAgeDisplay =
      eddString && encounter.period?.start
        ? (() => {
            const ga = calculateGestationalAgeInWeeksAndDays(eddString, encounter.period!.start);
            return ga.weeks >= 0 ? getGestationalAgeDisplay(ga) : undefined;
          })()
        : undefined;

    const encounterHasFlowsheet = hasEncounterFlowsheet(
      getCodeableConceptCode(encounter.serviceType),
    );

    return {
      checked: false,
      key: encounter.id!,
      weeks: gestationalAgeDisplay,
      encounterServiceType: encounterServiceTypeDisplay,
      encounterType: encounterTypeDisplay,
      encounterClass: encounterClassDisplay,
      encounterDate,
      episodeOfCare: episodeOfCare
        ? (getExtensionValue(episodeOfCare, Extensions.ArtifactTitle) as string)
        : undefined,
      encounterHasFlowsheet,
      flowsheetChecked: encounterHasFlowsheet ? true : undefined,
    };
  });
}

/**
 * Formats a diagnosis code for display in client export.
 * Uses formatCoding from Medplum core, with fallback to code or "Unknown".
 *
 * @param diagnosisCode - The diagnosis coding to format
 * @returns Formatted diagnosis string for display
 */
export function getDiagnosisDisplay(diagnosisCode: {
  code?: string;
  display?: string;
  system?: string;
}): string {
  return formatCoding(diagnosisCode) || diagnosisCode.code || "Unknown";
}

/**
 * Formats a Coding object for short display in the format "code - display".
 * Used for displaying diagnosis codes, CPT codes, and other coding values in client export.
 *
 * @param coding - The Coding object to format
 * @returns Formatted string in "code - display" format, or fallback values if missing
 */
export function formatCodingShort(coding?: { code?: string; display?: string }): string {
  if (!coding) return "-";
  const code = coding.code ?? "";
  const display = coding.display ?? "";

  if (code && display) return `${code} - ${display}`;
  return display || code || "-";
}

/**
 * Transforms lab order data into lab options for the export form.
 *
 * @param labOrdersData - Array of lab order data
 * @returns Array of LabOption objects
 */
export function mapLabOrderDataToOptions(labOrdersData: LabOrderData[]): LabOption[] {
  return labOrdersData.map(({ labOrder }) => {
    const labDate = labOrder.authoredOn
      ? format(new Date(labOrder.authoredOn), "MM/dd/yyyy")
      : undefined;

    const labName =
      labOrder.code?.coding
        ?.map((coding) => coding.display)
        .filter(Boolean)
        .join(", ") || "-";

    return {
      checked: false,
      key: labOrder.id!,
      labDate,
      labName,
    };
  });
}

/**
 * Transforms file data into file options for the export form.
 *
 * @param filesData - Array of file data
 * @returns Array of FileOption objects
 */
export function mapFileDataToOptions(filesData: FileData[]): FileOption[] {
  return filesData.map(({ documentReference }) => {
    const fileName = getIdentifier(documentReference, Systems.QH) ?? "";
    const fileType = documentReference.type?.coding?.[0]?.display ?? undefined;
    const dateAdded = documentReference.date ? usDateFormat(documentReference.date) : undefined;

    return {
      checked: false,
      key: documentReference.id!,
      fileName,
      fileType,
      dateAdded,
    };
  });
}
